'use client';

import { useMemo, useState } from 'react';

interface PageRow {
  title: string;
  url: string;
  description: string;
}

const inputClass =
  'w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 font-mk-body text-mk-ink placeholder:text-mk-muted/50 outline-none transition-colors focus:border-mk-pink/60';
const labelClass = 'mb-1.5 block font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-muted';

function buildLlmsTxt(opts: {
  siteName: string;
  summary: string;
  details: string;
  pages: PageRow[];
}): string {
  const name = opts.siteName.trim() || 'Your Site';
  const lines: string[] = [`# ${name}`, ''];

  if (opts.summary.trim()) {
    lines.push(`> ${opts.summary.trim()}`, '');
  }
  if (opts.details.trim()) {
    lines.push(opts.details.trim(), '');
  }

  const validPages = opts.pages.filter((p) => p.title.trim() && p.url.trim());
  if (validPages.length) {
    lines.push('## Key pages');
    for (const p of validPages) {
      const desc = p.description.trim() ? `: ${p.description.trim()}` : '';
      lines.push(`- [${p.title.trim()}](${p.url.trim()})${desc}`);
    }
    lines.push('');
  }

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd() + '\n';
}

export default function LlmsTxtGenerator() {
  const [siteName, setSiteName] = useState('');
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [pages, setPages] = useState<PageRow[]>([{ title: '', url: '', description: '' }]);
  const [copied, setCopied] = useState(false);

  const output = useMemo(
    () => buildLlmsTxt({ siteName, summary, details, pages }),
    [siteName, summary, details, pages],
  );

  function updatePage(index: number, field: keyof PageRow, value: string) {
    setPages((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  }
  function addPage() {
    setPages((prev) => [...prev, { title: '', url: '', description: '' }]);
  }
  function removePage(index: number) {
    setPages((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked, user can still select the preview */
    }
  }

  function download() {
    const blob = new Blob([output], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'llms.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="not-prose grid gap-8 lg:grid-cols-2">
      {/* Inputs */}
      <div className="space-y-5">
        <div>
          <label className={labelClass} htmlFor="site-name">
            Site name
          </label>
          <input
            id="site-name"
            className={inputClass}
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="Reclipt"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="summary">
            One-line summary
          </label>
          <input
            id="summary"
            className={inputClass}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="AI search engine for your video library."
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="details">
            Description (optional)
          </label>
          <textarea
            id="details"
            rows={3}
            className={inputClass}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="A short paragraph about what your site does and who it's for."
          />
        </div>

        <div>
          <span className={labelClass}>Key pages</span>
          <div className="space-y-3">
            {pages.map((page, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <div className="flex gap-2">
                  <input
                    aria-label={`Page ${i + 1} title`}
                    className={inputClass}
                    value={page.title}
                    onChange={(e) => updatePage(i, 'title', e.target.value)}
                    placeholder="Page title"
                  />
                  <button
                    type="button"
                    onClick={() => removePage(i)}
                    aria-label={`Remove page ${i + 1}`}
                    className="shrink-0 rounded-xl border border-white/10 px-3 text-mk-muted transition-colors hover:border-mk-pink/50 hover:text-mk-pink"
                  >
                    ×
                  </button>
                </div>
                <input
                  aria-label={`Page ${i + 1} URL`}
                  className={`${inputClass} mt-2`}
                  value={page.url}
                  onChange={(e) => updatePage(i, 'url', e.target.value)}
                  placeholder="https://example.com/page"
                />
                <input
                  aria-label={`Page ${i + 1} description`}
                  className={`${inputClass} mt-2`}
                  value={page.description}
                  onChange={(e) => updatePage(i, 'description', e.target.value)}
                  placeholder="What this page is (optional)"
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addPage}
            className="mt-3 rounded-xl border border-white/10 px-4 py-2 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-ink transition-colors hover:border-mk-pink/50 hover:text-mk-pink"
          >
            + Add page
          </button>
        </div>
      </div>

      {/* Preview */}
      <div className="lg:sticky lg:top-6 lg:self-start">
        <div className="mb-3 flex items-center justify-between">
          <span className={labelClass + ' mb-0'}>llms.txt preview</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={copy}
              className="rounded-lg border border-white/10 px-3 py-1.5 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-ink transition-colors hover:border-mk-pink/50"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={download}
              className="rounded-lg bg-mk-pink px-3 py-1.5 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-on-pink transition-opacity hover:opacity-90"
            >
              Download
            </button>
          </div>
        </div>
        <pre className="max-h-[28rem] overflow-auto rounded-2xl border border-white/10 bg-mk-s-lowest p-5 font-mono-ibm text-sm leading-relaxed text-mk-ink">
          {output}
        </pre>
        <p className="mt-3 font-mk-body text-xs text-mk-muted">
          Save this as <code className="text-mk-ink">llms.txt</code> and upload it to your site root so
          it&apos;s served at <code className="text-mk-ink">/llms.txt</code>.
        </p>
      </div>
    </div>
  );
}
